import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink, RefreshCw, Search, SearchX } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { AppButton, AppModal, Callout, Checkbox, EmptyState, Input, NativeSelect } from "../../ui";
import Shimmer from "../../Common/Shimmer";
import { DIFFICULTY_LABELS } from "./builderRules";

const PAGE_SIZE = 30;

const rowOf = (p) => ({
  question: String(p._id),
  title: p.title,
  difficulty: p.difficulty || "",
  languages: p.languages || [],
  sampleCount: p.sampleCount || 0,
  hiddenCount: p.hiddenCount || 0,
  marks: 10,
});

const tests = (p) => {
  const parts = [];
  if (p.sampleCount) parts.push(`${p.sampleCount} sample`);
  if (p.hiddenCount) parts.push(`${p.hiddenCount} hidden`);
  return parts.length ? `${parts.join(" + ")} test${p.sampleCount + p.hiddenCount === 1 ? "" : "s"}` : "";
};

const metaLine = (p) => [
  DIFFICULTY_LABELS[p.difficulty],
  (p.languages || []).slice(0, 4).join(", ") + ((p.languages || []).length > 4 ? "…" : ""),
  tests(p),
].filter(Boolean).join(" · ");

/**
 * Pick programming problems from the coding bank for one section.
 *
 * A problem with no test case cannot be marked, so it is listed with the reason
 * and cannot be ticked — the author learns why rather than not finding it. New
 * problems come in at 10 marks each; the section sets the marks.
 */
export default function CodingPickerDialog({
  open, onClose, onConfirm, sectionName, current = [], elsewhere, bankPath,
}) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [state, setState] = useState("idle");
  const [unavailable, setUnavailable] = useState("");
  const [picked, setPicked] = useState(() => new Map());
  const requestRef = useRef(0);

  useEffect(() => {
    if (open) setPicked(new Map(current.map((c) => [String(c.question), c])));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async (nextPage) => {
    const req = ++requestRef.current;
    setState(nextPage === 1 ? "loading" : "more");
    try {
      const params = { page: nextPage, limit: PAGE_SIZE };
      if (debounced) params.search = debounced;
      if (difficulty) params.difficulty = difficulty;
      const { data } = await apiClient.get("/assessments/coding-for-selection", { params });
      if (req !== requestRef.current) return;
      setUnavailable(data.available === false ? (data.reason || "Coding problems are not available.") : "");
      setRows((prev) => (nextPage === 1 ? data.problems || [] : [...prev, ...(data.problems || [])]));
      setTotal(data.total ?? 0);
      setPage(nextPage);
      setState("idle");
    } catch {
      if (req === requestRef.current) setState("error");
    }
  }, [debounced, difficulty]);

  useEffect(() => {
    if (open) load(1);
  }, [open, load]);

  const toggle = (p) => {
    const id = String(p._id);
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, rowOf(p));
      return next;
    });
  };

  const confirm = () => {
    const kept = current.filter((c) => picked.has(String(c.question)));
    const keptIds = new Set(kept.map((c) => String(c.question)));
    onConfirm([...kept, ...[...picked.values()].filter((c) => !keptIds.has(String(c.question)))]);
  };

  // The bank's difficulty values are capitalised ("Easy").
  const difficultyOptions = useMemo(() => Object.entries(DIFFICULTY_LABELS).map(([, l]) => l), []);

  return (
    <AppModal
      show={open}
      onHide={onClose}
      size="lg"
      title={`Add coding problems to ${sectionName}`}
      footer={(
        <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
          <span className="tw-text-[0.8125rem] tw-text-content-muted" role="status" aria-live="polite">
            {picked.size} selected
          </span>
          <div className="tw-flex tw-gap-2">
            <AppButton type="button" variant="outline" onClick={onClose}>Cancel</AppButton>
            <AppButton type="button" onClick={confirm} disabled={!!unavailable}>
              Use {picked.size} problem{picked.size === 1 ? "" : "s"}
            </AppButton>
          </div>
        </div>
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <p className="tw-m-0 tw-text-[0.8125rem] tw-leading-relaxed tw-text-content-muted">
          Students write a program in one of the problem's languages. While sitting they can run it
          against the sample tests; when they submit it is checked against every test, and the marks
          follow the tests it passes.
        </p>

        <div className="tw-flex tw-flex-wrap tw-gap-2">
          <div className="tw-min-w-[14rem] tw-flex-1">
            <Input
              type="search"
              aria-label="Search coding problems"
              placeholder="Search by title or label"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leading={<Search size={16} />}
              autoFocus
            />
          </div>
          <div className="tw-w-40">
            <NativeSelect aria-label="Difficulty" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
              <option value="">All levels</option>
              {difficultyOptions.map((l) => <option key={l} value={l}>{l}</option>)}
            </NativeSelect>
          </div>
          <AppButton type="button" variant="outline" onClick={() => load(1)} disabled={state === "loading"}>
            <RefreshCw size={16} aria-hidden="true" /> Refresh
          </AppButton>
          {bankPath && (
            <AppButton as="a" href={bankPath} target="_blank" rel="noopener noreferrer" variant="ghost">
              <ExternalLink size={16} aria-hidden="true" /> Open coding bank
            </AppButton>
          )}
        </div>

        {unavailable && <Callout tone="warning">{unavailable}</Callout>}
        {state === "error" && (
          <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={() => load(1)}>Try again</AppButton>}>
            The coding bank could not be loaded.
          </Callout>
        )}

        {state === "loading" ? (
          <Shimmer height={56} count={5} />
        ) : rows.length === 0 && !unavailable && state !== "error" ? (
          <EmptyState
            icon={SearchX}
            title={debounced || difficulty ? "No problems match" : "Your coding bank is empty"}
            description="Create a problem with test cases in the coding bank, then press Refresh here."
          />
        ) : rows.length > 0 && (
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-0">
            {rows.map((p) => {
              const id = String(p._id);
              const other = elsewhere?.get(id);
              const blocked = !p.ready || !!other;
              return (
                <li key={id} className="tw-px-3 tw-py-2.5 tw-border-0 tw-border-t tw-border-solid tw-border-line first:tw-border-t-0">
                  <Checkbox
                    checked={picked.has(id)}
                    onChange={() => toggle(p)}
                    disabled={blocked && !picked.has(id)}
                    label={p.title}
                    description={other ? `Already in ${other.name}` : p.ready ? metaLine(p) : `Cannot be marked yet — ${p.reason}`}
                    className="tw-w-full"
                  />
                </li>
              );
            })}
          </ul>
        )}

        {rows.length > 0 && rows.length < total && (
          <div className="tw-flex tw-justify-center">
            <AppButton type="button" variant="outline" size="sm" onClick={() => load(page + 1)} disabled={state === "more"}>
              {state === "more" ? "Loading…" : `Show more (${total - rows.length} left)`}
            </AppButton>
          </div>
        )}
      </div>
    </AppModal>
  );
}
